/**
 * Field-services panels for the Today screen: New quote / New booking buttons, shift and crew numbers, today's
 * bookings, crew rostered without a ticket they need (the client's field.tickets), and the cards of the
 * field-services modules a client has on (shift replies, unsigned dockets, invoices, SWMS sign-off). Each part
 * renders only when the client has the matching portal module or feature.
 */
import type { ReactNode } from "react";
import { BookingChip } from "../components/BookingChip";
import { Icon } from "../components/Icon";
import { Kpi } from "../components/Kpi";
import { plural } from "../components/PageHeader";
import { EmptyState, ErrorState, SkeletonLines } from "../components/States";
import { BRAND, hasModule, siteLink } from "../lib/config";
import { bookingsOnDay } from "../lib/data";
import { fmtTime } from "../lib/format";
import { crewLabel, crewNeeded, crewShortfall } from "../lib/jobs";
import { useAsync } from "../lib/useAsync";
import { billingInUse, BillingTodayCard } from "./billing/Invoices";
import { crewMessagesInUse, ShiftRepliesCard } from "./crew-messages/Messages";
import { docketsInUse, UnsignedDocketsCard } from "./field/Docket";
import { MissingTicketsCard, missingTicketsKpi, ticketsInUse, useMissingTickets } from "./field/MissingTickets";
import { SwmsDueCard, swmsSignoffInUse } from "./field/Swms";

export interface FieldToday {
  actions: ReactNode;
  kpisBefore: ReactNode;
  kpisAfter: ReactNode;
  /** Today's bookings (the main column); null when the bookings module is off. */
  main: ReactNode;
  side: ReactNode;
}

export function useFieldToday(): FieldToday {
  const showShifts = hasModule("bookings");
  const showCrew = hasModule("crew");
  const bookings = useAsync(() => (showShifts ? bookingsOnDay() : Promise.resolve(undefined)));
  // Crew record the tickets they hold (the client's field.tickets): the card shows who is rostered without one.
  const heldTickets = showCrew && ticketsInUse();
  const tickets = useMissingTickets();
  const newQuote = BRAND.instantQuote && hasModule("quotes");
  const newBooking = BRAND.bookingForm && showShifts;

  const live = bookings.data?.filter((b) => b.status !== "cancelled");
  const rostered = live ? new Set(live.flatMap((b) => b.allocations ?? []).map((a) => a.crew_member_id)).size : undefined;
  const needed = live?.reduce((n, b) => n + crewNeeded(b), 0);
  const short = live?.filter((b) => crewShortfall(b) !== null).length ?? 0;

  const actions = (newQuote || newBooking) && (
    <>
      {newQuote && (
        <a className="btn btn--outline btn--sm" href={siteLink("/instant-quote/")} target="_blank" rel="noopener">
          New quote <Icon name="external" size={16} /><span className="sr-only"> (opens the public quote form in a new tab)</span>
        </a>
      )}
      {newBooking && (
        <a className="btn btn--ink btn--sm" href={siteLink("/book-a-crew/")} target="_blank" rel="noopener">
          New booking <Icon name="external" size={16} /><span className="sr-only"> (opens the public booking form in a new tab)</span>
        </a>
      )}
    </>
  );

  const kpisBefore = showShifts && (
    <>
      <Kpi label="Shifts today" value={live?.length} loading={bookings.loading} failed={!!bookings.error}
        sub={live ? (short ? `${plural(short, "shift")} short of crew` : live.length ? "All confirmed shifts crewed" : "Nothing booked") : undefined} />
      <Kpi label="Controllers rostered" value={rostered} loading={bookings.loading} failed={!!bookings.error}
        sub={needed !== undefined ? (needed ? `of ${needed} needed today` : "No crew needed today") : undefined} />
    </>
  );

  const kpisAfter = heldTickets && <Kpi {...missingTicketsKpi(tickets)} alert />;

  const main = showShifts ? (
    <div className="card card--table today-grid__bookings">
      <div className="card__head">
        <h2 className="card__title">Bookings today</h2>
        <a className="card__link" href="#/bookings">Open bookings <Icon name="arrow" size={16} /></a>
      </div>
      {bookings.loading && <SkeletonLines rows={5} />}
      {bookings.error && <ErrorState message={bookings.error} onRetry={bookings.reload} />}
      {bookings.data && bookings.data.length === 0 && (
        <EmptyState icon="calendar" title="No bookings today">New booking requests from the website appear here.</EmptyState>
      )}
      {bookings.data && bookings.data.length > 0 && (
        <table className="table table--stack table--compact">
          <thead>
            <tr><th scope="col">Start</th><th scope="col">Site / customer</th><th scope="col">Crew</th><th scope="col">Status</th></tr>
          </thead>
          <tbody>
            {bookings.data.map((b) => (
              <tr key={b.id}>
                <td data-label="Start" className="strong nowrap">{fmtTime(b.starts_at)}</td>
                <td data-label="Site">
                  {b.site_address ?? "Site not set"}
                  <span className="sub">{b.company ?? b.contact_name ?? "No customer name"}</span>
                </td>
                <td data-label="Crew" className="nowrap">
                  {crewLabel(b)}
                  {b.allocations && b.status !== "requested" && <span className="sub">{b.allocations.length} of {crewNeeded(b)} allocated</span>}
                </td>
                <td data-label="Status"><BookingChip b={b} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  ) : null;

  const modules = (
    <>
      {crewMessagesInUse() && <ShiftRepliesCard />}
      {docketsInUse() && <UnsignedDocketsCard />}
      {billingInUse() && <BillingTodayCard />}
      {swmsSignoffInUse() && <SwmsDueCard />}
    </>
  );

  const side = (
    <>
      {modules}
      {heldTickets && <MissingTicketsCard state={tickets} />}
    </>
  );

  return { actions, kpisBefore, kpisAfter, main, side };
}
